import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Img from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage/LazyImage";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    mobileImage = "mobileImage",
    title = "title",
    heading = "heading",
    horizontalTextAlignment = "horizontalTextAlignment",
    verticalTextAlignment = "verticalTextAlignment",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    disableButton = "disableButton",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
    borderColor = "borderColor",
    gridImage = "gridImage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.mobileImage]: string;
        [fields.title]: string;
        [fields.heading]: string;
        [fields.verticalTextAlignment]: "top" | "center" | "bottom";
        [fields.horizontalTextAlignment]: "left" | "center" | "right";
        [fields.buttonLabel]: string;
        [fields.disableButton]: boolean;
        [fields.buttonLink]: LinkFieldValue;
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
        [fields.borderColor]: string;
        [fields.gridImage]: string;
    };
}

export interface SurStaticPageBannerStyles {
    heroBannerImageContainer?: InjectableCss;
    heroBannerImage?: LazyImageProps;
    bannerButton?: InjectableCss;
    bannerText?: InjectableCss;
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    heading?: InjectableCss;
    TextLeft?: InjectableCss;
    TextRight?: InjectableCss;
    container?: InjectableCss;
    bannerContainer?: InjectableCss;
}

export const surStaticPageBannerStyles: SurStaticPageBannerStyles = {
    heroBannerImageContainer: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;
            position: relative;
            margin-bottom: 40px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 0px;
                        padding: 20px;
                    `,
                    css`
                        margin-bottom: 0px;
                        padding: 20px;
                    `,
                    css`
                        margin-bottom: 0px;
                        padding: 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },

    heading: {
        css: css`
            > p {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.xxlarge};
                line-height: ${TYPOGRAPHY.lineHeight.xlmedium};
                color: ${getColor("primary.contrast")};
                text-transform: uppercase;
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-weight: ${TYPOGRAPHY.weight.bold};
                            line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                        `,
                        css`
                            font-weight: ${TYPOGRAPHY.weight.bold};
                            line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                        `,
                        css`
                            font-weight: ${TYPOGRAPHY.weight.bold};
                            line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: ${getColor("primary.contrast")};
            text-align: center;
        `,
    },
    container: {
        css: css`
            /* max-width: 720px; */
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            /* margin: 0 !important; */
        `,
    },
    bannerContainer: {
        css: css`
            position: relative;
            width: 100%;
        `,
    },
};

const styles = surStaticPageBannerStyles;

export const SurStaticPageBannerWidget: FC<OwnProps> = ({ fields }) => {
    const bannerButton = {
        css: css`
            cursor: auto;
            padding: 26px 40px;
            background: ${fields.backgroundColor};
            border-radius: 0px;
            border: 1px solid ${fields.borderColor};
            &:hover {
                background: ${fields.backgroundColor} !important;
                border: 1px solid ${fields.borderColor} !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 5px;
                    `,
                    css`
                        padding: 5px;
                    `,
                    null,
                    null,
                    null,
                ])}

            > span {
                font-family: "Mulish";
                font-style: normal;
                font-weight: 600;
                font-size: 26px;
                line-height: 34px;
                color: ${fields.textColor};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 20px;
                        `,
                        css`
                            font-size: 20px;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
        `,
    };
    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-repeat: no-repeat;
            width: 100%;
            height: auto;
            max-height: 540px;
            aspect-ratio: 2/1;
            object-fit: fill;
            background-size: cover;
            display: flex;
            justify-content: ${fields.verticalTextAlignment};
            align-items: center;
            flex-direction: column;
        `,
    };
    const MobileBackground = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-repeat: no-repeat;
            width: 100%;
            height: auto;
            aspect-ratio: 1/1;
            object-fit: cover;
            background-size: contain;
            display: flex;
            justify-content: ${fields.verticalTextAlignment};
            align-items: center;
            flex-direction: column;
        `,
    };

    const slideOverlayWrapperStyles = {
        css: css`
            display: flex;
            justify-content: center;
            align-items: ${fields.horizontalTextAlignment};
            flex-direction: column;
            width: 100%;
            padding: 40px;
            position: absolute;
            bottom: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 40px;
                        `,
                        css`
                            padding: 40px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    };

    const { url, title } = useGetLink(fields.buttonLink);
    const onClick = () => {
        if (!url) {
            return;
        }
        window.location.href = url;
    };

    return (
        <>
            <Hidden below="md">
                <StyledWrapper className="banner-wrapper max-width-container" {...styles.heroBannerImageContainer}>
                    <StyledWrapper className="banner-overlay-wrapper" {...styles.bannerContainer}>
                        <StyledWrapper>
                            <Img
                                src={fields.image}
                                {...overlayWrapperStyles}
                                altText={fields.buttonLabel || "Main Banner"}
                            />
                        </StyledWrapper>
                        {fields.buttonLabel && (
                            <StyledWrapper {...slideOverlayWrapperStyles}>
                                <StyledWrapper {...styles.container}>
                                    {fields.buttonLabel && (
                                        <Button {...bannerButton} role="button" aria-label={fields.buttonLabel}>
                                            {fields.buttonLabel}
                                        </Button>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
            <Hidden above="sm">
                <StyledWrapper {...styles.heroBannerImageContainer}>
                    <StyledWrapper className="banner-overlay-wrapper fullWidth" {...styles.bannerContainer}>
                        <Img
                            src={fields.mobileImage}
                            {...MobileBackground}
                            altText={fields.buttonLabel || "Main Banner"}
                        />

                        {fields.buttonLabel && (
                            <StyledWrapper {...slideOverlayWrapperStyles}>
                                <StyledWrapper {...styles.container}>
                                    <Button {...bannerButton} role="button" aria-label={fields.buttonLabel}>
                                        {fields.buttonLabel}
                                    </Button>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
        </>
    );
};

const surStaticPageBanner: WidgetModule = {
    component: SurStaticPageBannerWidget,
    definition: {
        group: "Basic",
        icon: "image",
        displayName: "SurStaticPageBanner",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.image,
                displayName: "Image for Desktop",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.mobileImage,
                displayName: "Image for Mobile",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            // {
            //     fieldType: "Translatable",
            //     name: fields.heading,
            //     editorTemplate: "RichTextField",
            //     displayName: "Heading",
            //     defaultValue: "",
            // },
            {
                fieldType: "General",
                name: fields.buttonLabel,
                displayName: "Banner Label",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            // {
            //     fieldType: "General",
            //     name: fields.buttonLink,
            //     displayName: "Button Link",
            //     editorTemplate: "LinkField",
            //     defaultValue: { type: "Page", value: "" },
            // },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.borderColor,
                displayName: "Border Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            // {
            //     name: fields.horizontalTextAlignment,
            //     editorTemplate: "DropDownField",
            //     defaultValue: "center",
            //     fieldType: "General",
            //     options: [
            //         { displayName: "Left", value: "flex-start; text-align: left" },
            //         { displayName: "Center", value: "center; text-align: center" },
            //         { displayName: "Right", value: "flex-end; text-align: right" },
            //     ],
            // },
            // {
            //     name: fields.verticalTextAlignment,
            //     editorTemplate: "DropDownField",
            //     defaultValue: "center",
            //     fieldType: "General",
            //     options: [
            //         { displayName: "Top", value: "flex-start" },
            //         { displayName: "Center", value: "center" },
            //         { displayName: "Bottom", value: "flex-end" },
            //     ],
            // },
        ],
    },
};

export default surStaticPageBanner;
